import { useCallback, useEffect, useState } from "react";
import { Trophy, Medal, Award, DollarSign, Loader2, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { getRankingVendedoras } from "@/lib/api";
import { fmtBRL } from "@/lib/format";
import { useAuth } from "@/lib/auth";

const MEDAL_STYLES = [
  { icon: Trophy, color: "text-amber-400", bg: "bg-amber-400/15", border: "border-amber-400/40" },
  { icon: Medal, color: "text-slate-300", bg: "bg-slate-300/15", border: "border-slate-300/40" },
  { icon: Award, color: "text-orange-500", bg: "bg-orange-500/15", border: "border-orange-500/40" },
];

/**
 * Top 3 vendedoras do mês por RECEITA (R$) + linha da própria vendedora
 * quando ela não estiver no pódio. Complementa o RankingVendasVendedor
 * (que ranqueia por qtd de clientes).
 */
export default function RankingReceitaVendedor() {
  const { user } = useAuth();
  const [data, setData] = useState({ ranking_receita: [], minha_posicao_receita: null });
  const [loading, setLoading] = useState(true);
  const mesAtual = new Date().toISOString().slice(0, 7);
  const mesLabel = new Date().toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getRankingVendedoras(mesAtual);
      setData({
        ranking_receita: r?.ranking_receita || [],
        minha_posicao_receita: r?.minha_posicao_receita || null,
      });
    } catch (e) {
      console.error("Erro ranking receita:", e);
    } finally { setLoading(false); }
  }, [mesAtual]);

  useEffect(() => { load(); }, [load]);

  const top3 = data.ranking_receita.slice(0, 3);
  const eu = user?.vendedor_name || "";
  const minha = data.minha_posicao_receita;
  const jaNoTop = minha && minha.posicao <= 3;

  return (
    <div className="rounded-xl border border-border bg-card p-5 card-flua" data-testid="ranking-receita-vendedor">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-emerald-500" strokeWidth={1.5} />
          <h3 className="font-heading font-semibold text-lg">Ranking por receita</h3>
        </div>
        <Badge variant="outline" className="text-[10px] capitalize">{mesLabel}</Badge>
      </div>

      {loading ? (
        <div className="text-center py-6 text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Calculando…
        </div>
      ) : top3.length === 0 ? (
        <div className="text-center py-6 text-sm text-muted-foreground">
          Nenhuma receita registrada neste mês ainda.
        </div>
      ) : (
        <div className="space-y-2">
          {top3.map((r, idx) => {
            const m = MEDAL_STYLES[idx];
            const Icon = m.icon;
            const isMe = eu && r.vendedor === eu;
            return (
              <div
                key={r.vendedor}
                data-testid={`ranking-receita-top-${idx + 1}`}
                className={`rounded-lg border-2 p-3 flex items-center gap-3 ${m.border} ${isMe ? "bg-emerald-500/10 ring-1 ring-emerald-500/40" : ""}`}
              >
                <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${m.bg}`}>
                  <Icon className={`w-5 h-5 ${m.color}`} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate">
                      {isMe ? "Você" : r.vendedor}
                    </span>
                    <Badge variant="outline" className="text-[10px]">#{idx + 1}</Badge>
                    {isMe && (
                      <Badge variant="outline" className="text-[10px] bg-emerald-500/15 text-emerald-500 border-emerald-500/40">
                        Sua posição
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading text-lg font-extrabold tabular-nums text-emerald-500">
                    {fmtBRL(r.receita)}
                  </div>
                  <div className="text-[10px] text-muted-foreground">receita</div>
                </div>
              </div>
            );
          })}

          {eu && minha && !jaNoTop && (
            <>
              <div className="relative flex items-center gap-2 py-1">
                <div className="flex-1 h-px bg-border" />
                <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Sua posição</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <div
                className="rounded-lg border-2 border-emerald-500/40 bg-emerald-500/10 p-3 flex items-center gap-3"
                data-testid="ranking-receita-eu"
              >
                <div className="w-10 h-10 rounded-lg grid place-items-center shrink-0 bg-emerald-500/15 text-emerald-500">
                  <User className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">Você</span>
                    <Badge variant="outline" className="text-[10px] bg-emerald-500/15 text-emerald-500 border-emerald-500/40">
                      #{minha.posicao}
                    </Badge>
                  </div>
                  {top3.length > 0 && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Faltam <strong className="text-foreground tabular-nums">{fmtBRL(Math.max(0, top3[2].receita - minha.receita + 0.01))}</strong> pra entrar no pódio
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-heading text-lg font-extrabold tabular-nums text-emerald-500">
                    {fmtBRL(minha.receita)}
                  </div>
                  <div className="text-[10px] text-muted-foreground">receita</div>
                </div>
              </div>
            </>
          )}

          {eu && !minha && (
            <div
              className="rounded-lg border-2 border-dashed border-border p-3 text-center text-xs text-muted-foreground"
              data-testid="ranking-receita-eu-zero"
            >
              Você ainda não tem receita neste mês.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
